import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Wizard, { IndexedStep, Navigator } from 'components/Wizard';
import * as mixpanelActions from 'redux/modules/mixpanel';
import { showError } from 'redux/modules/notification';
import { goToCreate } from 'redux/modules/router';
import { cancelVideoUpload } from 'redux/modules/video-upload';
import { Dispatch } from 'redux/types';
import { isApplicationError } from 'utils/ApplicationError';
import { FatalError } from 'utils/FatalError';
import { createChainedFunction } from 'utils/functions';
import { MIXPANEL_WIZARD_STEP_MAP, useVideoFlow, VideoFlowStep } from './flows';
import { ERROR_ID } from './flows/constants';
import useVideoWizardDefaults from './useVideoWizardDefaults';
import { block } from './utils';

const VideoWizard: React.FC = () => {
  const dispatch = useDispatch<Dispatch>();

  const {
    defaultAspectRatio,
    defaultLanguage,
    defaultObjective,
    defaultStep,
  } = useVideoWizardDefaults();

  const initialized = useRef(false);
  const [stepId, setStepId] = useState<VideoFlowStep>(
    defaultStep ?? VideoFlowStep.OBJECTIVE,
  );
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const wizardRef = useRef<Wizard<VideoFlowStep>>(null);

  useEffect(() => {
    if (defaultStep && wizardRef.current && !initialized.current) {
      initialized.current = true;
      wizardRef.current.jump(defaultStep);
    }
  }, [defaultStep]);

  useEffect(() => {
    return () => {
      dispatch(cancelVideoUpload());
    };
  }, [dispatch]);

  const handleError = useCallback(
    (error: Error) => {
      if (error instanceof FatalError) {
        dispatch(
          showError({
            message: 'There was an error creating your video',
            code: 'ER003',
            id: ERROR_ID,
          }),
        );
        dispatch(goToCreate());
        return;
      }

      dispatch(
        showError({
          message: error.message,
          code: isApplicationError(error) ? error.code : undefined,
        }),
      );
    },
    [dispatch],
  );

  const flow = useVideoFlow({
    defaultAspectRatio,
    defaultObjective,
    defaultLanguage,
    templateId,
    onTemplateSelect: setTemplateId,
    wizardRef,
    stepId,
    onError: handleError,
  });

  const handleStepChange = useCallback((to: IndexedStep<VideoFlowStep>) => {
    setStepId(to.stepId);
  }, []);

  const handleCancel = useCallback(
    (nav: Navigator) => {
      dispatch(
        mixpanelActions.onCancelWizard(
          'videoTranscription',
          MIXPANEL_WIZARD_STEP_MAP[nav.stepId],
        ),
      );
      dispatch(goToCreate());
    },
    [dispatch],
  );

  return (
    <Wizard
      {...flow}
      className={block()}
      onCancelClick={handleCancel}
      onStepChange={createChainedFunction(handleStepChange, flow.onStepChange)}
      ref={wizardRef}
    />
  );
};

export default VideoWizard;
